import { z } from "zod";

const KeywordIdSchema = z
  .string()
  .trim()
  .regex(/^[a-z][a-z0-9]*(?:-[a-z0-9]+)*$/)
  .max(80);

export const KeywordDefinitionSchema = z
  .object({
    id: KeywordIdSchema,
    name: z.string().trim().min(1).max(80),
    displayName: z.string().trim().min(1).max(80),
    shortDefinition: z.string().trim().min(1).max(280),
    fullDefinition: z.string().trim().min(1).max(2000),
    category: z.string().trim().min(1).max(120),
    timing: z.string().trim().min(1).max(500).optional(),
    examples: z.array(z.string().trim().min(1).max(500)).optional(),
    relatedKeywordIds: z.array(KeywordIdSchema).optional(),
    aliases: z.array(z.string().trim().min(1).max(80)).optional(),
    ruleReference: z.string().trim().min(1).max(160),
  })
  .strict();

export const KeywordRegistrySchema = z
  .array(KeywordDefinitionSchema)
  .min(1)
  .superRefine((keywords, context) => {
    const seen = new Map<string, number>();
    keywords.forEach((keyword, index) => {
      const previous = seen.get(keyword.id);
      if (previous === undefined) {
        seen.set(keyword.id, index);
      } else {
        context.addIssue({
          code: "custom",
          path: [index, "id"],
          message: `duplicates keyword[${String(previous)}].id`,
        });
      }
    });
  });

export const FATE_KEYWORDS = KeywordRegistrySchema.parse(
  [
    ["active-land", "Active Land", "An unresolved Land with Control above zero.", "Land status"],
    ["land-action", "Land Action", "Found or develop one Land during your turn.", "Land action"],
    [
      "deployment-action",
      "Deployment Action",
      "Deploy one Commander or Army during your turn.",
      "Deployment",
    ],
    ["direct", "Direct", "Attack the opposing Land in the same lane.", "Combat targeting"],
    [
      "flank",
      "Flank",
      "Attack another enemy lane when no opposing Active Land blocks the source.",
      "Combat targeting",
    ],
    [
      "preparing",
      "Preparing",
      "A card is visually queued while an authoritative outcome resolves.",
      "Presentation",
    ],
    ["ready", "Ready", "A Commander that may make its normal attack.", "Commander readiness"],
    [
      "spent",
      "Spent",
      "A Commander that has used its normal attack this turn.",
      "Commander readiness",
    ],
    [
      "committed",
      "Committed",
      "Cards on a claimed lane no longer affect active lanes.",
      "Lane defeat",
    ],
    ["control", "Control", "Land endurance; a Land is defeated when it reaches zero.", "Combat"],
    ["guard", "Guard", "Reduces incoming Land damage, potentially to zero.", "Combat"],
    ["army-size", "Army Size", "The printed size of an Army card.", "Army"],
    [
      "land-stage",
      "Land Stage",
      "The I, II, or III development level of a Land stack.",
      "Land development",
    ],
    [
      "kingdom-complete",
      "Kingdom Complete",
      "All three lanes have been founded at least once.",
      "Kingdom progress",
    ],
    [
      "no-territory",
      "No Territory",
      "A warned player with no Active Land loses when ending their response turn.",
      "Match end",
    ],
    ["exhaust", "Exhaust", "Move a card to the Exhaust zone; it does not recycle.", "Zones"],
    ["discard", "Discard", "Move a card to the discard pile, which may later recycle.", "Zones"],
    [
      "retrieve",
      "Retrieve",
      "Move a specified card from a named zone as an effect directs.",
      "Card effects",
    ],
    [
      "haste",
      "Haste",
      "Allows a card to bypass a printed deployment-readiness restriction.",
      "Card effects",
    ],
    [
      "event",
      "Event",
      "A card resolved during its owner's action window and then discarded.",
      "Events",
    ],
    [
      "event-limit",
      "Event Limit",
      "One base Event plus one per active Commander lane, maximum four.",
      "Events",
    ],
    ["upkeep", "Upkeep", "Local resources a Land pays for Armies stationed there.", "Supply"],
    ["found", "Found", "Play a Land into an empty friendly lane side.", "Land action"],
    ["develop", "Develop", "Add a matching Land copy to advance its Stage.", "Land action"],
    [
      "disband",
      "Disband",
      "Voluntarily move your stationed Army or Commander to discard.",
      "Action window",
    ],
  ].map(([id, name, shortDefinition, ruleReference]) => ({
    id,
    name,
    displayName: name,
    shortDefinition,
    fullDefinition: shortDefinition,
    category: ruleReference,
    ruleReference,
  })),
);

export const FATE_KEYWORD_IDS = new Set(FATE_KEYWORDS.map((keyword) => keyword.id));

export type KeywordDefinition = z.infer<typeof KeywordDefinitionSchema>;
export type KeywordId = KeywordDefinition["id"];
